<template>
    <div class="tw-relative tw-z-10" ref="container">
        <div>
            <p class="tw-text-sm">{{ $t('fields.delivery_status') }}</p>
            <button @click="visible = !visible"
                :class="[visible && '!tw-border-b']"
                class="tw-p-1.5 ltr:tw-pr-3 rtl:tw-pl-3 tw-cursor-pointer tw-mt-2 hover:tw-bg-gray-50 tw-duration-200 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-shadow-smx tw-h-[40px] tw-w-full tw-flex tw-items-center tw-relative">
    
                <div class="tw-flex tw-items-center tw-gap-2">
                    <div class="tw-text-xs tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                        <icon icon="solar:delivery-linear" class="tw-text-lg tw-text-gray-400" />
                        <div v-if="selectedStatus" class="tw-flex tw-items-center tw-gap-1">
                            <span :class="[getStatusColor(selectedStatus)]" class="tw-px-2 tw-py-0.5 tw-rounded tw-text-xs">
                                {{ $t(getStatusName(selectedStatus)) }}
                            </span>
                            <span class="tw-text-gray-400">•</span>
                            <span class="tw-text-xs tw-text-gray-600">
                                {{ $t(selectedDirection === 'asc' ? 'common.from-old-to-new' : 'common.from-new-to-old') }}
                            </span>
                        </div>
                        <div v-else class="tw-text-gray-500">
                            {{ $t('common.not-selected') }}
                        </div>
                    </div>
                    <icon
                        icon="iconamoon:arrow-down-2-light" 
                        :class="[
                            'tw-absolute tw-pointer-events-none tw-duration-200 tw-text-3xl tw-text-gray-400',
                            $i18n.locale == 'ar' ? 'tw-left-3.5' : 'tw-right-3.5'
                        ]" 
                        />
                </div>
            </button>
        </div>

        <transition class="tw-duration-200" enter-from-class="tw-translate-y-[10px] tw-opacity-0"
            leave-to-class="tw-translate-y-[10px] tw-opacity-0">
            <div v-if="visible" class="tw-duration-100">
                <div
                    class="tw-absolute tw-top-[calc(100%+5px)] ltr:tw-left-0 rtl:tw-right-0 tw-flex tw-flex-col tw-w-[320px] tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-300 tw-shadow-md">
                    
                    <!-- Delivery Status -->
                    <div class="tw-p-2 tw-border-b tw-border-solid tw-border-gray-200">
                        <div class="tw-text-xs tw-font-medium tw-mb-2 tw-text-gray-700">{{ $t('fields.delivery_status') }}</div>
                        <div class="tw-flex tw-gap-1 tw-flex-wrap">
                            <div v-for="status in deliveryStatuses" :key="status.value" role="button" @click="selectStatus(status.value)"
                                :class="[selectedStatus === status.value && '!tw-bg-primary-500 !tw-text-white !tw-border-primary-300']"
                                class="tw-h-[24px] tw-text-xs tw-px-2 tw-py-0.5 hover:tw-bg-primary-50 tw-duration-200 hover:tw-border-primary-200 tw-rounded-sm tw-border tw-border-solid tw-border-gray-200 tw-w-fit tw-text-gray-600 tw-font-medium tw-flex tw-items-center tw-gap-1">
                                <div :class="[status.color]" class="tw-w-1.5 tw-h-1.5 tw-rounded-full"></div>
                                {{ $t(status.name) }}
                            </div>
                        </div>
                    </div>

                    <!-- Sorting Direction -->
                    <div class="tw-p-2 tw-border-b tw-border-solid tw-border-gray-200">
                        <div class="tw-text-xs tw-font-medium tw-mb-2 tw-text-gray-700">{{ $t('fields.sorting_direction') }}</div>
                        <div class="tw-flex tw-gap-1">
                            <div v-for="direction in sortingDirections" :key="direction.value" role="button" @click="selectDirection(direction.value)"
                                :class="[selectedDirection === direction.value && '!tw-bg-primary-500 !tw-text-white !tw-border-primary-300']"
                                class="tw-h-[24px] tw-text-xs tw-px-2 tw-py-0.5 hover:tw-bg-primary-50 tw-duration-200 hover:tw-border-primary-200 tw-rounded-sm tw-border tw-border-solid tw-border-gray-200 tw-w-fit tw-text-gray-600 tw-font-medium tw-flex tw-items-center tw-gap-1">
                                <icon :icon="direction.value === 'asc' ? 'solar:sort-from-top-to-bottom-linear' : 'solar:sort-from-bottom-to-top-linear'" class="tw-text-xs" />
                                {{ $t(direction.label) }}
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="tw-p-2 tw-flex tw-items-center tw-justify-between">
                        <button @click="clearFilter" class="tw-text-xs tw-text-gray-500 hover:tw-text-red-500 tw-duration-200">
                            {{ $t('buttons.clear') }}
                        </button>
                        <div class="tw-flex tw-gap-1">
                            <a-button @click="visible = false" intent="text" class="!tw-w-[60px] !tw-h-[24px] !tw-min-h-0 tw-text-xs">
                                {{ $t('buttons.cancel') }}
                            </a-button>
                            <a-button @click="applyFilter" class="!tw-w-[60px] !tw-h-[24px] !tw-min-h-0 tw-text-xs">
                                {{ $t('buttons.filter') }}
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { order_deliveries } from '@/config/status';
import { computed, ref, watch, inject } from 'vue';
import { useI18n } from "vue-i18n";
import { onClickOutside } from '@vueuse/core';

const i18n = useI18n();
const updateFilters = inject('update:filters')
const filters = inject('filters')

const container = ref(null);
onClickOutside(container, () => visible.value = false);

// Delivery status options from config
const deliveryStatuses = computed(() => order_deliveries.map(c => ({ ...c, label: i18n.t(c.name) })))

// Sorting direction options
const sortingDirections = computed(() => [
    { value: 'asc', label: 'common.from-old-to-new' },
    { value: 'desc', label: 'common.from-new-to-old' }
])

const visible = ref(false)

// Initialize with current filter values or defaults
const selectedStatus = ref(filters.value.delivery_status_by_date?.value || null)
const selectedDirection = ref(filters.value.delivery_status_by_date?.sorting_direction || 'asc')

// Helper functions
const getStatusName = (statusValue) => {
    const status = deliveryStatuses.value.find(s => s.value === statusValue)
    return status ? status.name : ''
}

const getStatusColor = (statusValue) => {
    const status = deliveryStatuses.value.find(s => s.value === statusValue)
    return status ? status.color : 'tw-bg-gray-100 tw-text-gray-600'
}

// Selection functions
const selectStatus = (status) => {
    selectedStatus.value = status
}

const selectDirection = (direction) => {
    selectedDirection.value = direction
}

const clearFilter = () => {
    selectedStatus.value = null
    selectedDirection.value = 'asc'
}

const applyFilter = () => {
    const newFilters = {
        ...filters.value,
        delivery_status_by_date: selectedStatus.value ? {
            value: selectedStatus.value,
            sorting_direction: selectedDirection.value
        } : null
    }

    updateFilters(newFilters)
    visible.value = false
}

// Watch for external filter changes
watch(() => filters.value.delivery_status_by_date, (newValue) => {
    if (newValue) {
        selectedStatus.value = newValue.value
        selectedDirection.value = newValue.sorting_direction
    } else {
        selectedStatus.value = null
        selectedDirection.value = 'asc'
    }
}, { deep: true, immediate: true })
</script>

<style></style>